const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL as string;

const TOKEN_KEY = 'coala.session.token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function call<T>(fn: 'auth' | 'employee' | 'manager', action: string, payload: Record<string, unknown> = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(`${FUNCTIONS_URL}/${fn}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ action, ...payload }),
  });
  let body: any = null;
  try { body = await res.json(); } catch { /* no body */ }
  if (!res.ok) {
    if (res.status === 401) clearToken();
    throw new ApiError(body?.error || `Erro ${res.status}`, res.status);
  }
  return body as T;
}

export const authApi = {
  loginEmployee: (telefone: string, pin: string) => call<{ token: string; employee: any }>('auth', 'login-employee', { telefone, pin }),
  loginManager: (password: string) => call<{ token: string }>('auth', 'login-manager', { password }),
  resetPin: (cpf: string, telefone: string, novoPin: string) => call<{ token: string; employee: any }>('auth', 'reset-pin', { cpf, telefone, novoPin }),
};

export const employeeApi = {
  me: () => call<{
    employee: any; consent: any; signature: any; documentPhoto: any; profilePhotoUrl: string | null; termoTexto: string; toleranciaMin: number;
    tipoHorario: 'esperado' | 'turno' | 'manual'; turnoDiurno: any; turnoNoturno: any; vtValorPadrao: string | null; vtDataPadrao: string | null;
  }>('employee', 'me'),
  acceptConsent: (payload: Record<string, unknown>) => call('employee', 'consent', payload),
  saveSignature: (payload: Record<string, unknown>) => call('employee', 'save-signature', payload),
  saveDocumentPhoto: (payload: Record<string, unknown>) => call('employee', 'save-document-photo', payload),
  savePushSubscription: (subscription: unknown) => call('employee', 'save-push-subscription', { subscription }),
  getPonto: (date: string) => call<{ record: Record<string, any> }>('employee', 'get-ponto', { date }),
  punch: (payload: Record<string, unknown>) => call<{ record: any }>('employee', 'punch', payload),
  getValeAlim: (date: string) => call<{ record: any }>('employee', 'get-vale-alim', { date }),
  registrarValeAlim: (payload: Record<string, unknown>) => call('employee', 'register-vale-alim', payload),
  getRecebimento: (tipo: string) => call<{ record: any }>('employee', 'get-recebimento', { tipo }),
  confirmRecebimento: (payload: Record<string, unknown>) => call('employee', 'register-recebimento', payload),
  listDocumentos: () => call<{ documentos: any[] }>('employee', 'list-documentos'),
  getDocumentoAssinatura: (documentoId: string) => call<{ assinatura: any }>('employee', 'get-documento-assinatura', { documentoId }),
  signDocumento: (payload: Record<string, unknown>) => call('employee', 'sign-documento', payload),
  triggerSos: (payload: { lat: number | null; lng: number | null; accuracy: number | null; local: string | null }) => call<{ ok: boolean; alertId: string; criadoEm: string }>('employee', 'trigger-sos', payload),
  changePin: (novoPin: string) => call('employee', 'change-pin', { novoPin }),
};

export const managerApi = {
  loginCheck: () => call('manager', 'ping'),
  listEmployees: () => call<{ employees: any[] }>('manager', 'list-employees'),
  addEmployee: (payload: Record<string, unknown>) => call<{ employee: any }>('manager', 'add-employee', payload),
  updateEmployee: (payload: Record<string, unknown>) => call('manager', 'update-employee', payload),
  toggleEmployee: (id: string) => call('manager', 'toggle-employee', { id }),
  getProfiles: () => call<{ profiles: any[] }>('manager', 'get-profiles'),
  getSignedUrl: (path: string) => call<{ url: string }>('manager', 'get-signed-url', { path }),
  getPontoLog: () => call<{ rows: any[] }>('manager', 'get-ponto-log'),
  getAlimLog: () => call<{ rows: any[] }>('manager', 'get-alim-log'),
  getRecebimentosLog: () => call<{ rows: any[] }>('manager', 'get-recebimentos-log'),
  listDocumentos: () => call<{ documentos: any[] }>('manager', 'list-documentos'),
  addDocumento: (payload: Record<string, unknown>) => call('manager', 'add-documento', payload),
  deleteDocumento: (id: string) => call('manager', 'delete-documento', { id }),
  getDocumentoStatus: (id: string) => call<{ status: any[] }>('manager', 'get-documento-status', { id }),
  getConfig: () => call<{ config: any }>('manager', 'get-config'),
  updateConfig: (payload: Record<string, unknown>) => call('manager', 'update-config', payload),
  verifySchedulePassword: (senha: string) => call('manager', 'verify-schedule-password', { senha }),
  verifyOwnerAccess: (senha: string) => call('manager', 'verify-owner-access', { senha }),
  verifySpecialAccess: (senha: string) => call('manager', 'verify-special-access', { senha }),
  listAuditLog: (senha: string) => call<{ logs: any[] }>('manager', 'list-audit-log', { senha }),
  exportBackup: (senha: string) => call<Record<string, unknown>>('manager', 'export-backup', { senha }),
  deleteEmployees: (senha: string, target: { todos: true } | { ids: string[] }) => call<{ ok: boolean; excluidos: number }>('manager', 'delete-employees', { senha, ...target }),
  previewTurno: (turno: 'diurno' | 'noturno') => call<{ horarios: Record<string, string> }>('manager', 'preview-turno', { turno }),
  listManagerPasswords: () => call<{ passwords: { id: string; criadoEm: string }[] }>('manager', 'list-manager-passwords'),
  addManagerPassword: (password: string) => call('manager', 'add-manager-password', { password }),
  removeManagerPassword: (id: string) => call('manager', 'remove-manager-password', { id }),
  sendEmployeeLogin: (id: string, canal: 'sms' | 'whatsapp' = 'sms') => call<{ sucesso: boolean; detalhe: string }>('manager', 'send-employee-login', { id, canal }),
  listPendingReminders: (camarote?: string) => call<{ pendentes: any[] }>('manager', 'list-pending-reminders', camarote ? { camarote } : {}),
  sendPushReminder: (ids: string[]) => call<{ results: any[] }>('manager', 'send-push-reminder', { ids }),
  sendPushBroadcast: (ids: string[], mensagem: string) => call<{ results: any[] }>('manager', 'send-push-broadcast', { ids, mensagem }),
  savePushSubscription: (subscription: unknown) => call('manager', 'save-push-subscription', { subscription }),
  suggestPin: (telefone: string, excludeId?: string) => call<{ pin: string }>('manager', 'suggest-pin', { telefone, excludeId }),
  getDashboard: () => call<{ totalAtivos: number; pontoCompleto: number; pontoPendente: number; alimPendente: number; recvPendente: number; sosAbertos: number; pinResets: any[] }>('manager', 'get-dashboard'),
  listPinResets: () => call<{ resets: any[] }>('manager', 'list-pin-resets'),
  getLiveStatus: () => call<{ date: string; employees: any[] }>('manager', 'get-live-status'),
  listSosAlerts: () => call<{ alerts: any[] }>('manager', 'list-sos-alerts'),
  resolveSosAlert: (id: string) => call('manager', 'resolve-sos-alert', { id }),
};
